import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Match } from "@/types/domain";
import type { MatchDetail } from "@/types/match-detail";

interface FetchOpts {
  date?: string; // YYYY-MM-DD
  league?: number;
  search?: string;
  live?: boolean;
  next?: number;
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD
  timezone?: string;
  /** Année de la saison (ex. 2026 pour Coupe du Monde 2026). Requis par
   * api-football pour les compétitions à édition unique (Mondial, Euro). */
  season?: number;
}

export function useMatches(opts: FetchOpts = {}) {
  return useQuery({
    queryKey: ["matches", opts],
    refetchInterval: opts.live ? 60_000 : 5 * 60_000,
    queryFn: async (): Promise<Match[]> => {
      const { data, error } = await supabase.functions.invoke<{ matches: Match[] }>(
        "api-football-fixtures",
        { body: opts }
      );
      if (error) throw error;
      return data?.matches ?? [];
    },
  });
}

export function useMatch(matchId: number | undefined) {
  return useQuery({
    queryKey: ["match", matchId],
    enabled: !!matchId,
    refetchInterval: 60_000,
    queryFn: async (): Promise<Match | null> => {
      const { data, error } = await supabase.functions.invoke<{ match: Match }>(
        "api-football-fixtures",
        { body: { matchId } }
      );
      if (error) throw error;
      return data?.match ?? null;
    },
  });
}

export function useMatchDetail(matchId: number | undefined) {
  return useQuery({
    queryKey: ["match-detail", matchId],
    enabled: !!matchId,
    refetchInterval: (q) => {
      const status = q.state.data?.fixture.fixture.status.short;
      if (!status) return 60_000;
      const finished = ["FT", "AET", "PEN", "CANC", "ABD"].includes(status);
      return finished ? false : 60_000;
    },
    queryFn: async (): Promise<MatchDetail | null> => {
      const { data, error } = await supabase.functions.invoke<{ match: MatchDetail }>(
        "api-football-match",
        { body: { matchId } }
      );
      if (error) throw error;
      return data?.match ?? null;
    },
  });
}

/**
 * Récupère plusieurs matchs par id en un seul appel (cache 1 min côté
 * Edge Function pour les lives, 5 min sinon). Utilisé pour enrichir les
 * coupons (bets ne stockent que matchId, pas les infos du match).
 */
export function useMatchesByIds(ids: number[]) {
  const key = [...new Set(ids)].sort((a, b) => a - b);
  return useQuery({
    queryKey: ["matches", "by-ids", key],
    enabled: key.length > 0,
    refetchInterval: 60_000,
    queryFn: async (): Promise<Match[]> => {
      const { data, error } = await supabase.functions.invoke<{ matches: Match[] }>(
        "api-football-fixtures",
        { body: { ids: key } }
      );
      if (error) throw error;
      return data?.matches ?? [];
    },
  });
}

export function useFeaturedMatches() {
  return useQuery({
    queryKey: ["featured-matches"],
    refetchInterval: 5 * 60_000,
    queryFn: async (): Promise<Match[]> => {
      const { data: featured } = await supabase
        .from("featured_matches")
        .select("match_id")
        .gt("expires_at", new Date().toISOString())
        .order("priority", { ascending: false })
        .limit(5);

      if (!featured?.length) return [];
      const ids = featured.map((f) => f.match_id);
      const { data } = await supabase.functions.invoke<{ matches: Match[] }>(
        "api-football-fixtures",
        { body: { ids } }
      );
      return data?.matches ?? [];
    },
  });
}
